// src/components/Screening/DisqualifyingStep.jsx
import React, { useState } from 'react'
import { AlertTriangle, CheckCircle } from 'lucide-react'
import GlowingButton from '../Common/GlowingButton'

const DISQUALIFYING_CRITERIA = {
  'no-photo': {
    title: 'Pas de photo sur CV',
    description: 'CV sans photo sera automatiquement éliminé',
    severity: 'high'
  },
  'spelling-errors': {
    title: 'Graves fautes d\'orthographe',
    description: 'Plus de 3 fautes d\'orthographe majeures',
    severity: 'high'
  },
  'too-long': {
    title: 'CV de plus de 3 pages',
    description: 'CV dépassant 3 pages sera éliminé',
    severity: 'medium'
  },
  'no-contact': {
    title: 'Coordonnées incomplètes',
    description: 'Absence d\'email ET de téléphone',
    severity: 'high'
  },
  'no-experience': {
    title: 'Aucune expérience professionnelle',
    description: 'Candidat sans expérience pro',
    severity: 'medium'
  }
}

const DisqualifyingStep = ({ criteria, onCriteriaChange, onNext, onPrev }) => {
  const [enableDisqualifying, setEnableDisqualifying] = useState(
    Object.keys(criteria).some(key => key.startsWith('disqualifying_') && criteria[key])
  )

  const handleToggleDisqualifying = (enabled) => {
    setEnableDisqualifying(enabled)
    if (!enabled) {
      Object.keys(DISQUALIFYING_CRITERIA).forEach(key => {
        onCriteriaChange(`disqualifying_${key}`, false)
      })
    }
  }

  const selectedCriteria = Object.entries(criteria)
    .filter(([key, value]) => key.startsWith('disqualifying_') && value)
    .map(([key]) => key.replace('disqualifying_', ''))

  return (
    <div className="space-y-8">
      <div className="text-center">
        <div className="relative inline-block mb-6">
          <div className="absolute inset-0 bg-gradient-to-r from-red-500 to-orange-500 rounded-3xl blur-lg opacity-50 animate-pulse"></div>
          <div className="relative w-20 h-20 bg-gradient-to-r from-red-500 to-orange-500 rounded-3xl flex items-center justify-center shadow-2xl">
            <AlertTriangle className="w-10 h-10 text-white" />
          </div>
        </div>
        <h2 className="text-4xl font-bold text-white mb-4">Critères Disqualifiants</h2>
        <p className="text-gray-400 text-lg max-w-3xl mx-auto leading-relaxed">
          Définissez les critères qui éliminent automatiquement un candidat
        </p>
      </div>

      <div className="max-w-4xl mx-auto space-y-6">
        {/* Activation */}
        <div className="glass-card p-6">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-xl font-bold text-white mb-2">
                Souhaitez-vous activer des critères disqualifiants ?
              </h3>
              <p className="text-gray-400">
                Ces critères élimineront automatiquement les candidats qui ne les respectent pas
              </p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={enableDisqualifying}
                onChange={(e) => handleToggleDisqualifying(e.target.checked)}
                className="sr-only"
              />
              <div className={`w-14 h-8 rounded-full transition-colors ${
                enableDisqualifying ? 'bg-red-500' : 'bg-gray-600'
              }`}>
                <div className={`w-6 h-6 bg-white rounded-full mt-1 ml-1 transition-transform ${
                  enableDisqualifying ? 'translate-x-6' : ''
                }`}></div>
              </div>
            </label>
          </div>
        </div>

        {/* Critères */}
        {enableDisqualifying && (
          <div className="space-y-4">
            <h4 className="text-lg font-semibold text-white mb-4">
              Sélectionnez les critères disqualifiants :
            </h4>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {Object.entries(DISQUALIFYING_CRITERIA).map(([key, criterion]) => (
                <label
                  key={key}
                  className={`flex items-start p-4 border rounded-xl cursor-pointer transition-all hover:scale-105 ${
                    criteria[`disqualifying_${key}`]
                      ? 'border-red-500 bg-red-500/10 shadow-lg shadow-red-500/25'
                      : 'border-gray-600 hover:border-red-400 hover:bg-red-500/5'
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={criteria[`disqualifying_${key}`] || false}
                    onChange={(e) => onCriteriaChange(`disqualifying_${key}`, e.target.checked)}
                    className="mt-1 mr-3 w-4 h-4 text-red-600 bg-gray-700 border-gray-600 rounded focus:ring-red-500"
                  />
                  <div className="flex-1">
                    <div className="flex items-center mb-1">
                      <span className="text-white font-medium">{criterion.title}</span>
                      <span className={`ml-2 px-2 py-1 text-xs rounded-full ${
                        criterion.severity === 'high' 
                          ? 'bg-red-500/20 text-red-300' 
                          : 'bg-orange-500/20 text-orange-300'
                      }`}>
                        {criterion.severity === 'high' ? 'Critique' : 'Important'}
                      </span>
                    </div>
                    <p className="text-gray-400 text-sm">{criterion.description}</p>
                  </div>
                </label>
              ))}
            </div>

            {/* Résumé */}
            {selectedCriteria.length > 0 && (
              <div className="mt-6 p-4 bg-red-500/10 border border-red-500/20 rounded-lg">
                <h5 className="text-red-300 font-medium mb-2 flex items-center">
                  <AlertTriangle className="w-5 h-5 mr-2" />
                  {selectedCriteria.length} critère(s) disqualifiant(s) activé(s) :
                </h5>
                <div className="space-y-1">
                  {selectedCriteria.map((key) => {
                    const criterion = DISQUALIFYING_CRITERIA[key]
                    return (
                      <div key={key} className="text-red-200 text-sm flex items-center">
                        <span className="mr-2">❌</span>
                        {criterion.title}
                      </div>
                    )
                  })}
                </div>
                <div className="mt-3 p-3 bg-red-600/20 rounded-lg">
                  <p className="text-red-200 text-sm font-medium">
                    ⚠️ Les candidats ne respectant pas ces critères seront automatiquement éliminés avec un score de 0.
                  </p>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Message si aucun critère */}
        {!enableDisqualifying && (
          <div className="text-center p-8 bg-green-500/10 border border-green-500/20 rounded-lg">
            <h5 className="text-green-300 font-medium mb-2">✅ Aucun critère disqualifiant</h5>
            <p className="text-green-200 text-sm">
              Tous les candidats seront évalués selon les critères de tri et techniques.
            </p>
          </div>
        )}
      </div>

      <div className="flex justify-center space-x-4 pt-8">
        <GlowingButton variant="secondary" onClick={onPrev}>
          ← Retour
        </GlowingButton>
        <GlowingButton onClick={onNext}>
          Continuer →
        </GlowingButton>
      </div>
    </div>
  )
}

export default DisqualifyingStep